/**
 * SoleadLab 2.0 — Dock Mission API Client (F-03E-1)
 *
 * Frontend service for the F-03 dock mission CRUD + export endpoints.
 * Pure transport layer: encodes URL params, injects bodies, surfaces
 * `If-Match` for the version-concurrency endpoints, parses the `ETag`
 * response header into a numeric `version`, and exposes a typed
 * `isDock3ExportGatedError` helper so callers can branch the UI on the
 * Dock 3 export gate.
 *
 * F-03E-1 ships **only** the service surface — no store, no UI wiring.
 * The store + UI work lives in F-03E-2 / F-03E-3 / F-03E-4. Mirrors the
 * `claimApi.ts` shape for consistency with other versioned-CRUD
 * services in the repo.
 *
 * Backend contract (per F-03C / F-03D-3):
 *   - List response is slim and omits `plan_json`.
 *   - Get/create/patch responses return the full row including parsed
 *     `plan_json` (already an object — not a stringified JSON).
 *   - Responses use snake_case fields matching D1 columns.
 *   - Request bodies use camelCase.
 *   - GET single / POST / PATCH return an `ETag: <integer>` header
 *     carrying the mission row's `version`.
 *   - PATCH / DELETE / export require `If-Match: <version>`.
 *   - Export increments `kmz_version` but does NOT bump the mission row
 *     `version`. The response carries the unchanged row `version`, the
 *     newly-bumped `kmz_version`, and a `download_url` signed for the
 *     existing backend TTL (1 hour at time of writing).
 *   - Dock 3 export returns 422 with `error.code === 'DOCK3_EXPORT_NOT_YET_SUPPORTED'`.
 *   - Export success carries the verbatim `warning_copy`:
 *     "Preview/export only. Confirm this mission in DJI Pilot 2 or
 *      FlightHub 2 before flight."
 */

import { apiFetch, apiFetchWithEtag, ApiError } from './apiFetch';
export { isConflictError, ApiError } from './apiFetch';

// ─── Compatibility unions ──────────────────────────────────────────────────

export type TargetDockModel = 'dock2' | 'dock3';

export type TargetAircraftModel =
  | 'matrice_3d'
  | 'matrice_3td'
  | 'matrice_4d'
  | 'matrice_4td';

export type CompatibilityProfileVersion = 'v1';

/**
 * Opaque persisted plan shape. F-03B treats `plan_json` as opaque TEXT
 * at the DB layer (the backend's only structural gates are JSON
 * round-tripability + the 256 KB serialized cap). F-03D-1's WPML
 * generator owns the strict shape contract at export time; this
 * service stays generic on purpose so future plan_json schema
 * extensions don't churn the service surface.
 */
export type DockMissionPlanJson = Record<string, unknown>;

// ─── DB row + summary projections ──────────────────────────────────────────

/**
 * Full mission row returned by GET single / POST / PATCH / GET (the
 * server parses `plan_json` from TEXT into an object before sending —
 * no double-parse on the client).
 */
export interface DockMissionRow {
  id: string;
  org_id: string;
  site_id: string;
  name: string;
  description: string | null;
  plan_json: DockMissionPlanJson;
  target_dock_model: TargetDockModel;
  target_aircraft_model: TargetAircraftModel;
  compatibility_profile_version: string;
  kmz_r2_key: string | null;
  kmz_size_bytes: number | null;
  kmz_version: number;
  kmz_generated_at: string | null;
  version: number;
  created_by: string | null;
  updated_by: string | null;
  created_at: string;
  updated_at: string;
}

/**
 * Slim list projection — F-03C list endpoint deliberately omits
 * `plan_json` to keep list responses bounded.
 */
export type DockMissionSummary = Omit<DockMissionRow, 'plan_json'>;

// ─── Request payloads ──────────────────────────────────────────────────────

export interface CreateDockMissionPayload {
  name: string;
  description?: string | null;
  planJson: DockMissionPlanJson;
  targetDockModel: TargetDockModel;
  targetAircraftModel: TargetAircraftModel;
  compatibilityProfileVersion?: CompatibilityProfileVersion;
}

export interface UpdateDockMissionPayload {
  name?: string;
  description?: string | null;
  planJson?: DockMissionPlanJson;
  targetDockModel?: TargetDockModel;
  targetAircraftModel?: TargetAircraftModel;
  compatibilityProfileVersion?: CompatibilityProfileVersion;
}

// ─── Export response ───────────────────────────────────────────────────────

/**
 * Exact F-03D-3 export envelope. `version` is the mission row's version
 * (unchanged across export). `kmz_version` is the monotonic export
 * counter (bumped on every successful export). `download_url` is a
 * `generateSignedAssetUrl` output with the existing 1-hour TTL.
 * `warning_copy` MUST be rendered verbatim by F-03E UI surfaces.
 */
export interface DockMissionExportResult {
  id: string;
  version: number;
  kmz_version: number;
  kmz_r2_key: string;
  kmz_size_bytes: number;
  kmz_generated_at: string;
  download_url: string;
  content_type: string;
  warning_copy: string;
}

// ─── ETag-bearing envelope (mirror of claimApi pattern) ────────────────────

export interface DockMissionWithVersion {
  data: DockMissionRow;
  version: number;
}

// ─── Path helpers ──────────────────────────────────────────────────────────

function basePath(siteId: string): string {
  return `/api/v2/sites/${encodeURIComponent(siteId)}/dock-missions`;
}

function missionPath(siteId: string, missionId: string): string {
  return `${basePath(siteId)}/${encodeURIComponent(missionId)}`;
}

function ifMatchHeader(expectedVersion: number): Record<string, string> {
  return { 'If-Match': String(expectedVersion) };
}

/**
 * Resolve the mission row's `version` for the public `{ data, version }`
 * envelope.
 *
 * Source-of-truth ladder:
 *   1. The `ETag` response header (parsed by `apiFetchWithEtag` into the
 *      `etag` number). This is the canonical signal documented in the
 *      F-03C backend contract.
 *   2. **Fallback:** `data.version` from the response body, which the
 *      F-03C backend always populates with the same value as the ETag.
 *
 * The fallback exists because the renderer-side fetch Response sometimes
 * surfaces `headers.get('ETag')` as `null` even when the backend set it.
 * Observed paths:
 *   - Vite dev proxy → Electron renderer: the proxy round-trip can drop
 *     non-listed `Access-Control-Expose-Headers` entries under specific
 *     dev-server configurations.
 *   - Cross-origin fetches whose CORS preflight didn't expose `ETag`
 *     (defensive — production CORS in `utils/response.ts` DOES expose it).
 *   - Service-worker or intermediate cache stripping.
 * Hydrating with `version: 0` triggers the backend's `MISSING_IF_MATCH`
 * guard on the next mutation (mission row versions start at 1), so we
 * read the body version when the header signal is unusable.
 *
 * If BOTH signals are missing/invalid, the response is structurally
 * malformed — we throw an `ApiError` rather than silently shipping a
 * zero version that would deadlock the optimistic-concurrency path.
 */
function resolveDockMissionVersion(envelope: {
  data: DockMissionRow;
  etag: number;
}): DockMissionWithVersion {
  const { data, etag } = envelope;

  if (Number.isFinite(etag) && etag > 0) {
    return { data, version: etag };
  }

  const bodyVersion = data?.version;
  if (
    typeof bodyVersion === 'number'
    && Number.isFinite(bodyVersion)
    && bodyVersion > 0
  ) {
    return { data, version: bodyVersion };
  }

  throw new ApiError(
    'Dock mission response is missing a valid version (neither the ETag header nor the response body carried a positive integer).',
    0,
    'INVALID_MISSION_VERSION',
    { etag, bodyVersion },
  );
}

// ─── List ──────────────────────────────────────────────────────────────────

/**
 * GET /api/v2/sites/:siteId/dock-missions
 *
 * Returns the slim list projection (no `plan_json`). The list endpoint
 * does not carry an `ETag` header — callers wanting the row `version`
 * must follow up with `getDockMission` for the specific mission they
 * intend to mutate.
 */
export async function listDockMissions(
  siteId: string,
): Promise<DockMissionSummary[]> {
  return apiFetch<DockMissionSummary[]>(basePath(siteId));
}

// ─── Get single ────────────────────────────────────────────────────────────

/**
 * GET /api/v2/sites/:siteId/dock-missions/:missionId
 *
 * Returns the full row + the row's current `version`. The version is
 * resolved by `resolveDockMissionVersion`, which prefers the `ETag`
 * response header and falls back to `data.version` from the body when
 * the header signal is missing/unusable (see helper docstring). The
 * returned `version` is what the caller passes to subsequent PATCH /
 * DELETE / export as `If-Match`.
 */
export async function getDockMission(
  siteId: string,
  missionId: string,
): Promise<DockMissionWithVersion> {
  return resolveDockMissionVersion(
    await apiFetchWithEtag<DockMissionRow>(missionPath(siteId, missionId)),
  );
}

// ─── Create ────────────────────────────────────────────────────────────────

/**
 * POST /api/v2/sites/:siteId/dock-missions
 *
 * The returned `version` is `1` (initial), resolved via
 * `resolveDockMissionVersion` (ETag preferred, body-version fallback).
 * Body is camelCase per the F-03C `CreateDockMissionSchema`.
 */
export async function createDockMission(
  siteId: string,
  payload: CreateDockMissionPayload,
): Promise<DockMissionWithVersion> {
  return resolveDockMissionVersion(
    await apiFetchWithEtag<DockMissionRow>(basePath(siteId), {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  );
}

// ─── Update ────────────────────────────────────────────────────────────────

/**
 * PATCH /api/v2/sites/:siteId/dock-missions/:missionId
 *
 * Optimistic concurrency: `expectedVersion` ships as the `If-Match`
 * header. The backend rejects with 409 `VERSION_CONFLICT` if the row's
 * current `version` doesn't match — callers should `isConflictError`
 * the throw and re-fetch via `getDockMission`. Body is the partial
 * camelCase patch from `UpdateDockMissionPayload`. The post-PATCH
 * `version` is resolved by `resolveDockMissionVersion` (ETag preferred,
 * body-version fallback).
 */
export async function updateDockMission(
  siteId: string,
  missionId: string,
  patch: UpdateDockMissionPayload,
  expectedVersion: number,
): Promise<DockMissionWithVersion> {
  return resolveDockMissionVersion(
    await apiFetchWithEtag<DockMissionRow>(missionPath(siteId, missionId), {
      method: 'PATCH',
      headers: ifMatchHeader(expectedVersion),
      body: JSON.stringify(patch),
    }),
  );
}

// ─── Delete ────────────────────────────────────────────────────────────────

/**
 * DELETE /api/v2/sites/:siteId/dock-missions/:missionId
 *
 * Optimistic concurrency via `If-Match`. F-03D-4 best-effort cleans the
 * R2 prefix after the D1 row delete; failures there don't propagate to
 * the response, which always carries `{ id, deleted: true }` on
 * success.
 */
export async function deleteDockMission(
  siteId: string,
  missionId: string,
  expectedVersion: number,
): Promise<{ id: string; deleted: true }> {
  return apiFetch<{ id: string; deleted: true }>(
    missionPath(siteId, missionId),
    {
      method: 'DELETE',
      headers: ifMatchHeader(expectedVersion),
    },
  );
}

// ─── Export KMZ ────────────────────────────────────────────────────────────

/**
 * POST /api/v2/sites/:siteId/dock-missions/:missionId/export-kmz
 *
 * `expectedVersion` is the mission row's `version` (not `kmz_version`).
 * Export does NOT bump the mission row `version` — re-export from the
 * same client without an intervening PATCH continues to use the same
 * `If-Match` value.
 *
 * Maps `WpmlExportError` codes to typed 422 envelopes. Use
 * `isDock3ExportGatedError(err)` to distinguish the Dock 3 gate from
 * generic 422s.
 *
 * The response's `warning_copy` MUST be rendered verbatim by callers —
 * see the F-03E diagnosis decision row #7.
 */
export async function exportDockMissionKmz(
  siteId: string,
  missionId: string,
  expectedVersion: number,
): Promise<DockMissionExportResult> {
  return apiFetch<DockMissionExportResult>(
    `${missionPath(siteId, missionId)}/export-kmz`,
    {
      method: 'POST',
      headers: ifMatchHeader(expectedVersion),
    },
  );
}

// ─── Typed error helpers ───────────────────────────────────────────────────

/**
 * Returns true iff `err` is the typed 422 envelope the F-03D-3 endpoint
 * emits when a Dock 3 mission's export reaches the
 * `DOCK3_EXPORT_NOT_YET_SUPPORTED` profile gate. F-03E UI surfaces
 * should render the gated banner instead of a generic validation
 * error.
 *
 * Defensive: matches against `ApiError` and any plain-error shape that
 * carries the same `.status` + `.code` fields.
 */
export function isDock3ExportGatedError(err: unknown): boolean {
  if (err instanceof ApiError) {
    return err.status === 422 && err.code === 'DOCK3_EXPORT_NOT_YET_SUPPORTED';
  }
  if (err && typeof err === 'object') {
    const candidate = err as { status?: unknown; code?: unknown };
    return (
      candidate.status === 422
      && candidate.code === 'DOCK3_EXPORT_NOT_YET_SUPPORTED'
    );
  }
  return false;
}
